import { useState } from "react";
import API from "../lib/api";
import Reveal from "./Reveal";
import { compressImage } from "../lib/image";
import { useLang } from "../i18n/LanguageContext";

export default function Commission() {
  const { lang, t } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [story, setStory] = useState("");
  const [photo, setPhoto] = useState(null);
  const [hp, setHp] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setError(false);
    try {
      const form = new FormData();
      form.append("name", name);
      form.append("email", email);
      form.append("object_story", story);
      form.append("language", lang);
      form.append("website", hp);
      if (photo) {
        const prepared = await compressImage(photo);
        form.append("photo", prepared);
      }
      await API.post("/inquiries", form);
      setState("success");
    } catch (err) {
      setState("idle");
      setError(true);
    }
  };

  return (
    <section id="commission" className="bg-cream py-24 sm:py-32" data-testid="commission-section">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="ttwk-label" data-testid="commission-eyebrow">
              {t.commission.eyebrow}
            </p>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              {t.commission.heading}
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-umber/80">{t.commission.sub}</p>
            <p
              className="mt-10 max-w-md border-l-2 border-terracotta pl-6 font-serif text-lg italic leading-relaxed text-espresso"
              data-testid="commission-lede"
            >
              {t.commission.lede}
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          {state === "success" ? (
            <div className="flex h-full flex-col items-start justify-center py-16" data-testid="commission-success">
              <p className="font-serif text-3xl font-light italic leading-snug sm:text-4xl">{t.commission.success}</p>
              <p className="mt-4 text-sm text-umber/70">{t.commission.successSub}</p>
            </div>
          ) : (
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="flex flex-col gap-9" data-testid="commission-form">
                <div>
                  <label htmlFor="c-name" className="ttwk-label">
                    {t.commission.fieldName}
                  </label>
                  <input
                    id="c-name"
                    data-testid="commission-name-input"
                    className="ttwk-input mt-2"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    maxLength={120}
                  />
                </div>

                <div>
                  <label htmlFor="c-email" className="ttwk-label">
                    {t.commission.fieldEmail}
                  </label>
                  <input
                    id="c-email"
                    type="email"
                    data-testid="commission-email-input"
                    className="ttwk-input mt-2"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="c-story" className="ttwk-label">
                    {t.commission.fieldStory}
                  </label>
                  <textarea
                    id="c-story"
                    data-testid="commission-story-input"
                    className="ttwk-input mt-2 min-h-[140px] resize-y"
                    placeholder={t.commission.storyPlaceholder}
                    value={story}
                    onChange={(e) => setStory(e.target.value)}
                    required
                    minLength={10}
                    maxLength={5000}
                  />
                </div>

                <div>
                  <span className="ttwk-label">
                    {t.commission.fieldPhoto}{" "}
                    <span className="ml-1 normal-case tracking-normal text-umber/50">({t.commission.optional})</span>
                  </span>
                  <label
                    htmlFor="c-photo"
                    className="mt-3 inline-flex cursor-pointer items-center rounded-full border border-clay px-6 py-3 text-[11px] uppercase tracking-[0.18em] text-umber transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
                    data-testid="commission-photo-label"
                  >
                    {photo ? photo.name : t.commission.fieldPhoto}
                  </label>
                  <input
                    id="c-photo"
                    type="file"
                    accept="image/*"
                    data-testid="commission-photo-input"
                    className="hidden"
                    onChange={(e) => setPhoto(e.target.files && e.target.files[0])}
                  />
                  <p className="mt-3 text-sm text-umber/55">{t.commission.photoHelp}</p>
                </div>

                <input
                  className="honeypot"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={hp}
                  onChange={(e) => setHp(e.target.value)}
                  data-testid="commission-honeypot"
                />

                {error && (
                  <p className="text-sm text-terracotta" data-testid="commission-error">
                    {t.commission.error}
                  </p>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={state === "sending"}
                    data-testid="commission-submit-button"
                    className="rounded-full bg-terracotta px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-espresso disabled:opacity-50"
                  >
                    {state === "sending" ? t.commission.sending : t.commission.submit}
                  </button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
